import React from "react";
import { HashLink } from "react-router-hash-link";
import { ArrowDown } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import DotField from "./DotField";

const EASE = [0.22, 1, 0.36, 1];

/**
 * A breathing pause between the hero and the product story — the wordmark
 * fills the screen for a beat before the workflow deep-dive begins, the way
 * a brand card lands between a cold-open and a demo. No video: the button
 * below is a real anchor into the pinned story, not a decorative dead end.
 */
export default function LogoInterstitial() {
  const reduceMotion = useReducedMotion();

  return (
    <section
      style={{
        position: "relative",
        minHeight: "min(76vh, 620px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background:
          "radial-gradient(ellipse 80% 60% at 50% 40%, #f5f8ff 0%, #ffffff 62%)",
      }}
    >
      <DotField intensity={0.85} spacingScale={1.1} />

      <div
        className="container"
        style={{ position: "relative", zIndex: 2, textAlign: "center" }}
      >
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.9, ease: EASE }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "clamp(14px, 2.4vw, 26px)",
          }}
        >
          <img
            src="assets/1024.png"
            alt=""
            aria-hidden="true"
            style={{
              width: "clamp(48px, 6vw, 76px)",
              height: "clamp(48px, 6vw, 76px)",
              borderRadius: "18px",
              boxShadow: "0 12px 30px -10px rgba(37, 99, 235, 0.35)",
            }}
          />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              fontSize: "clamp(2.1rem, 6vw, 4.4rem)",
              letterSpacing: "-0.03em",
              color: "var(--text-main)",
            }}
          >
            EMIBOOK
          </span>
        </motion.div>

        <motion.p
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          style={{
            marginTop: "22px",
            marginBottom: 0,
            fontSize: "clamp(1rem, 1.6vw, 1.2rem)",
            color: "var(--text-muted)",
          }}
        >
          EMI and loan management, end to end.
        </motion.p>

        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.28 }}
          style={{ marginTop: "34px" }}
        >
          <HashLink
            smooth
            to="#how-it-works"
            className="eb-btn eb-btn-secondary"
            style={{ background: "#ffffff" }}
          >
            See it in action
            <motion.span
              animate={reduceMotion ? {} : { y: [0, 4, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              style={{ display: "inline-flex" }}
            >
              <ArrowDown size={16} />
            </motion.span>
          </HashLink>
        </motion.div>
      </div>
    </section>
  );
}
